import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Skeleton} from "primereact/skeleton";
import {Toast} from "primereact/toast";
import {formsApi, formsErrorKey} from "./forms-api";
import {ClearableDropdown, DROPDOWN_PT, Icon, PERSIAN_RANGE_CONFIG, ZofinoDatepicker, useFormat, useT} from "./host";
import {Field, RunTag, Section, SUBMISSION_STATUSES, SubmissionTag, useOutputsText} from "./forms-ui";

// Responses of one form (inside the builder) or of all forms (the «responses» tab), the detail dialog with identity
// resolution / retry / review, and the CSV export filtered by form, date range and status.
const toDate = (value) => {
    if (!value) return null;
    const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};
const dayStart = (date) => { const out = new Date(date); out.setHours(0, 0, 0, 0); return out; };
const dayEnd = (date) => { const out = new Date(date); out.setHours(23, 59, 59, 999); return out; };
const RANGES = ["all", "today", "7", "30", "90", "custom"];

function rangeParams(range, custom) {
    const now = new Date();
    if (range === "today") return {from: dayStart(now).toISOString(), to: dayEnd(now).toISOString()};
    if (["7", "30", "90"].includes(range)) return {from: dayStart(new Date(now.getTime() - (Number(range) - 1) * 86400000)).toISOString(), to: dayEnd(now).toISOString()};
    if (range === "custom") {
        const start = toDate(custom?.start);
        const end = toDate(custom?.end) || start;
        return {from: start ? dayStart(start).toISOString() : undefined, to: end ? dayEnd(end).toISOString() : undefined};
    }
    return {};
}

function DateRangeFilter({range, custom, onChange, className = ""}) {
    const t = useT();
    const format = useFormat();
    const [picking, setPicking] = useState(false);
    const customLabel = range === "custom" && toDate(custom?.start)
        ? t("forms.range.between", {from: format.date(toDate(custom.start), false), to: format.date(toDate(custom.end) || toDate(custom.start), false)})
        : null;
    return (
        <>
            <Dropdown className={`text-xs h-3rem border-round-lg align-items-center ${className}`.trim()} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={range}
                      options={RANGES.map((value) => ({value, label: t(`forms.range.${value}`)}))}
                      valueTemplate={(option) => <span className="text-xs">{customLabel || option?.label || t("forms.range.all")}</span>}
                      onChange={(event) => (event.value === "custom" ? setPicking(true) : onChange(event.value, null))}/>
            <Dialog header={<span className="text-sm">{t("forms.range.pick")}</span>} visible={picking} onHide={() => setPicking(false)} dismissableMask
                    className="rtl w-11 md:w-auto" headerClassName="border-bottom-1 border-200 py-3" contentClassName="p-2">
                {picking && (
                    <ZofinoDatepicker value={custom || {start: null, end: null}} config={PERSIAN_RANGE_CONFIG}
                                      onApply={({value}) => { setPicking(false); onChange(toDate(value?.start) ? "custom" : "all", value); }}
                                      onCancel={() => setPicking(false)}/>
                )}
            </Dialog>
        </>
    );
}

export function ExportDialog({visible, onHide, forms, initialForm, initialRange = "30", initialCustom = null, toast}) {
    const t = useT();
    const format = useFormat();
    const [form, setForm] = useState(initialForm || null);
    const [range, setRange] = useState(initialRange);
    const [custom, setCustom] = useState(initialCustom);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (visible) { setForm(initialForm || null); setRange(initialRange); setCustom(initialCustom); setStatus(null); }
    }, [visible, initialForm, initialRange, initialCustom]);
    const download = async () => {
        setBusy(true);
        try {
            const file = await formsApi.exportCsv({form: form || undefined, status: status || undefined, ...rangeParams(range, custom)});
            const url = URL.createObjectURL(file.blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = file.filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            toast?.current?.show({severity: "success", summary: t("forms.export.done", {n: format.number(file.rows)}), life: 3000, className: "rtl text-xs"});
            onHide();
        } catch (error) {
            toast?.current?.show({severity: "error", summary: t(formsErrorKey(error, "forms.export.failed")), className: "rtl text-xs"});
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog visible={visible} onHide={onHide} className="rtl w-11 md:w-6 lg:w-4 border-round-bottom-2xl"
                header={<span className="text-sm">{t("forms.export.title")}</span>} headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3" contentClassName="p-4"
                pt={{footer: () => ({className: "border-round-bottom-2xl"})}}
                footer={(
                    <div className="flex gap-2 justify-content-end pt-3">
                        <Button type="button" text className="text-xs" label={t("forms.cancel")} onClick={onHide}/>
                        <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:download" className="text-base"/>} label={t("forms.export.download")} loading={busy} onClick={download}/>
                    </div>
                )}>
            <div className="flex flex-column gap-3">
                <Field label={t("forms.export.form")} hint={t("forms.export.form-hint")}>
                    <ClearableDropdown value={form} placeholder={t("forms.filter.all-forms")} filter className="text-xs h-3rem border-round-lg align-items-center" panelClassName="rtl" pt={DROPDOWN_PT}
                                       options={forms.map((item) => ({value: item.key, label: item.title}))} clearAriaLabel={t("forms.filter.clear")} onChange={(event) => setForm(event.value)}/>
                </Field>
                <Field label={t("forms.export.range")}>
                    <DateRangeFilter className="w-full" range={range} custom={custom} onChange={(next, value) => { setRange(next); setCustom(value); }}/>
                </Field>
                <Field label={t("forms.export.status")}>
                    <ClearableDropdown value={status} placeholder={t("forms.export.status-all")} className="text-xs h-3rem border-round-lg align-items-center" panelClassName="rtl" pt={DROPDOWN_PT}
                                       options={SUBMISSION_STATUSES.map((value) => ({value, label: t(`forms.submission-status.${value}`)}))} clearAriaLabel={t("forms.filter.clear")} onChange={(event) => setStatus(event.value)}/>
                </Field>
                <span className="text-xs text-400 line-height-3">{t("forms.export.note")}</span>
            </div>
        </Dialog>
    );
}

// yes / no and consent come from the server as raw values and are translated here
const OUTPUT_OF = {customer_destination: "customer", update_customer: "customer", create_task: "task", notify: "notify"};
const outputsOfRuns = (runs) => {
    const list = [...new Set(runs.filter((item) => item.status === "succeeded").map((item) => OUTPUT_OF[item.type]).filter(Boolean))];
    return list.length ? list : ["responses"];
};
const InfoLine = ({label, children}) => (
    <div className="flex justify-content-between align-items-center gap-2 text-xs">
        <span className="text-500">{label}</span>
        <span className="text-800 text-left">{children}</span>
    </div>
);

const answerText = (t, answer) => {
    if (answer.type === "yes_no" && !answer.sensitive) return t(`forms.answer.${answer.raw === "no" ? "no" : "yes"}`);
    if (answer.type === "consent" && !answer.sensitive) return t(answer.raw ? "forms.answer.accepted" : "forms.answer.not-accepted");
    return String(answer.value ?? "—");
};

function SubmissionDrawer({submissionKey, onClose, onChanged, canManage}) {
    const t = useT();
    const format = useFormat();
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState("");
    const [note, setNote] = useState("");
    useEffect(() => {
        setDetail(null);
        setError("");
        setNote("");
        if (submissionKey) formsApi.submission(submissionKey).then(setDetail).catch((loadError) => setError(formsErrorKey(loadError)));
    }, [submissionKey]);
    const run = async (key, work) => {
        setBusy(key);
        setError("");
        try {
            setDetail(await work());
            onChanged?.();
        } catch (actError) {
            setError(formsErrorKey(actError));
        } finally {
            setBusy("");
        }
    };
    const outputsText = useOutputsText();
    const scores = Object.entries(detail?.scores || {}).filter(([, score]) => score && score.counted);
    const actionable = detail && canManage && detail.source !== "test" && detail.runs.some((item) => item.status === "failed" || item.status === "needs_review");
    return (
        <Dialog visible={Boolean(submissionKey)} onHide={onClose} dismissableMask className="rtl w-11 md:w-8 lg:w-6 xl:w-5 border-round-bottom-2xl"
                header={<span className="text-sm">{t("forms.detail.title")}</span>} headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                contentClassName="surface-ground p-3 border-round-bottom-2xl">
            {error && <Message severity="error" className="w-full justify-content-start text-xs mb-2" text={t(error)}/>}
            {!detail && !error && <Skeleton height="12rem" className="border-round-2xl"/>}
            {detail && (
                <div className="flex flex-column gap-3">
                    <div className="grid m-0">
                        <div className="col-12 md:col-6 p-1">
                            <Section title={t("forms.detail.respondent")} className="h-full">
                                <strong className="text-sm text-900">{detail.respondent || t("forms.detail.no-name")}</strong>
                                <InfoLine label={t("forms.column.form")}>{detail.form?.title || "—"}</InfoLine>
                            </Section>
                        </div>
                        <div className="col-12 md:col-6 p-1">
                            <Section title={t("forms.detail.submission")} className="h-full">
                                <InfoLine label={t("forms.column.status")}><SubmissionTag status={detail.status}/></InfoLine>
                                <InfoLine label={t("forms.column.submitted")}>{format.date(detail.submittedAt)}</InfoLine>
                                <InfoLine label={t("forms.column.version")}>{detail.version ? t("forms.version", {n: format.number(detail.version)}) : "—"}</InfoLine>
                                <InfoLine label={t("forms.ending.tracking")}><strong dir="ltr">{detail.tracking}</strong></InfoLine>
                            </Section>
                        </div>
                    </div>
                    <Section title={t("forms.detail.output")}>
                        <span className="flex align-items-center gap-2 text-xs text-800"><Icon icon="mi mi:move_to_inbox" className="text-base text-primary"/>{outputsText(outputsOfRuns(detail.runs))}</span>
                    </Section>
                    <Section title={t("forms.detail.answers")}>
                        {detail.answers.length === 0 && <span className="text-xs text-500">{t("forms.detail.no-answers")}</span>}
                        {detail.answers.map((answer) => (
                            <div key={answer.key} className="flex flex-column gap-1">
                                <span className="text-xs text-500">{answer.label}</span>
                                {answer.type === "file" && Array.isArray(answer.files)
                                    ? answer.files.map((file) => <span key={file.id} className="flex align-items-center gap-2 text-xs"><Icon icon="mi mi:attach_file" className="text-base text-500"/>{file.name}</span>)
                                    : <span className="text-sm text-800 white-space-pre-line" dir={["phone", "email"].includes(answer.type) ? "ltr" : undefined}>
                                        {answerText(t, answer)}
                                    </span>}
                            </div>
                        ))}
                    </Section>
                    {scores.length > 0 && (
                        <Section title={t("forms.detail.scores")}>
                            {scores.map(([key, score]) => (
                                <div key={key} className="flex justify-content-between text-xs">
                                    <span className="text-600">{score.label || key}</span>
                                    <span className="text-800">{format.number(score.normalized ?? score.raw)}{score.bandLabel ? ` · ${score.bandLabel}` : ""}</span>
                                </div>
                            ))}
                        </Section>
                    )}
                    <Section title={t("forms.detail.identity")}>
                        <span className="text-xs text-700">{t(`forms.identity.${detail.identity || "skipped"}`)}{detail.customer?.name ? ` — ${detail.customer.name}` : ""}</span>
                        {detail.identity === "conflict" && canManage && (
                            <div className="flex flex-column gap-2">
                                <span className="text-xs text-500 line-height-3">{t("forms.detail.conflict-help")}</span>
                                {detail.candidates.map((candidate) => (
                                    <Button key={candidate.key} type="button" outlined className="text-xs gap-2 justify-content-start" loading={busy === candidate.key}
                                            icon={<Icon icon="mi mi:person" className="text-base"/>}
                                            label={t("forms.detail.candidate", {name: candidate.name || t("forms.detail.no-name"), by: t(`forms.customer-field.${candidate.by}`)})}
                                            onClick={() => run(candidate.key, () => formsApi.resolveIdentity(detail.key, candidate.key))}/>
                                ))}
                                <div className="flex gap-2">
                                    <Button type="button" text className="text-xs gap-2" icon={<Icon icon="mi mi:person_add" className="text-base"/>} label={t("forms.detail.new-customer")} loading={busy === "new"} onClick={() => run("new", () => formsApi.resolveIdentity(detail.key, "new"))}/>
                                    <Button type="button" text severity="secondary" className="text-xs gap-2" icon={<Icon icon="mi mi:link_off" className="text-base"/>} label={t("forms.detail.no-link")} loading={busy === "none"} onClick={() => run("none", () => formsApi.resolveIdentity(detail.key, "none"))}/>
                                </div>
                            </div>
                        )}
                    </Section>
                    {detail.runs.length > 0 && (
                        <Section title={t("forms.detail.history")} end={actionable && (
                            <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:refresh" className="text-base"/>} label={t("forms.detail.retry")} loading={busy === "retry"} onClick={() => run("retry", () => formsApi.retry(detail.key))}/>
                        )}>
                            {detail.runs.map((item, index) => (
                                <div key={item.key} className="flex align-items-center gap-2 text-xs">
                                    <span className={`w-2rem h-2rem border-circle flex align-items-center justify-content-center flex-none ${item.status === "succeeded" ? "bg-green-100 text-green-700" : "surface-200 text-700"}`}>
                                        {item.status === "succeeded" ? <Icon icon="mi mi:check" className="text-sm"/> : format.number(index + 1)}
                                    </span>
                                    <span className="flex-1 text-700">{t(`forms.run.${item.type}`)}</span>
                                    {item.attempts > 1 && <span className="text-400">{t("forms.detail.attempts", {n: format.number(item.attempts)})}</span>}
                                    <RunTag status={item.status}/>
                                </div>
                            ))}
                        </Section>
                    )}
                    <Section title={t("forms.detail.notes")}>
                        {(detail.notes || []).map((item, index) => (
                            <div key={index} className="flex flex-column gap-1 surface-50 border-round-lg p-2">
                                <span className="text-xs text-400">{item.by} · {format.date(item.at)}</span>
                                <span className="text-xs text-800 white-space-pre-line">{item.text}</span>
                            </div>
                        ))}
                        {canManage && (
                            <>
                                <InputTextarea className="text-xs border-round-lg" value={note} onChange={(event) => setNote(event.target.value)} rows={2} autoResize placeholder={t("forms.detail.note-placeholder")}/>
                                <div className="flex gap-2">
                                    <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:note_add" className="text-base"/>} label={t("forms.detail.add-note")} disabled={!note.trim()} loading={busy === "note"}
                                            onClick={() => run("note", async () => { const next = await formsApi.review(detail.key, {note}); setNote(""); return next; })}/>
                                    {!detail.reviewed && <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:done" className="text-base"/>} label={t("forms.detail.mark-reviewed")} loading={busy === "review"} onClick={() => run("review", () => formsApi.review(detail.key, {reviewed: true}))}/>}
                                </div>
                            </>
                        )}
                    </Section>
                </div>
            )}
        </Dialog>
    );
}

export default function Submissions({forms = [], formKey = null, canManage, initialSubmission = null}) {
    const t = useT();
    const format = useFormat();
    const toast = useRef(null);
    const [filters, setFilters] = useState({form: formKey, status: null, q: "", range: "all", custom: null});
    const [page, setPage] = useState({first: 0, rows: 25});
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [open, setOpen] = useState(initialSubmission);
    const [exporting, setExporting] = useState(false);
    const outputsText = useOutputsText();

    const latest = useRef(0);
    const load = useCallback(() => {
        // only the newest request may set the list (a slow earlier one never overwrites it)
        const request = ++latest.current;
        setError("");
        formsApi.submissions({
            form: filters.form || undefined, status: filters.status || undefined, q: filters.q || undefined,
            ...rangeParams(filters.range, filters.custom), page: page.first / page.rows + 1, pageSize: page.rows,
        }).then((next) => { if (request === latest.current) setData(next); })
            .catch((loadError) => { if (request === latest.current) setError(formsErrorKey(loadError)); });
    }, [filters, page]);
    useEffect(() => {
        const timer = setTimeout(load, filters.q ? 300 : 0);
        return () => clearTimeout(timer);
    }, [load, filters.q]);
    const update = (patch) => { setFilters((current) => ({...current, ...patch})); setPage((current) => ({...current, first: 0})); };

    return (
        <div className="flex flex-column gap-3">
            <Toast ref={toast} position="bottom-left"/>
            <section role="search" aria-label={t("forms.filters")} className="flex flex-column lg:flex-row lg:align-items-center gap-2">
                <IconField className="align-items-center flex flex-1 min-w-0">
                    <InputIcon className="pi pi-search"/>
                    <InputText className="w-full h-3rem border-round-lg pt-2 pb-2 text-sm" value={filters.q} placeholder={t("forms.responses.search")} onChange={(event) => update({q: event.target.value})}/>
                </IconField>
                <div className="flex flex-wrap md:flex-nowrap gap-2">
                    {!formKey && (
                        <div className="w-full md:w-12rem">
                            <ClearableDropdown value={filters.form} placeholder={t("forms.filter.all-forms")} filter className="text-xs h-3rem border-round-lg align-items-center" panelClassName="rtl" pt={DROPDOWN_PT}
                                               options={forms.map((item) => ({value: item.key, label: item.title}))} clearAriaLabel={t("forms.filter.clear")} onChange={(event) => update({form: event.value})}/>
                        </div>
                    )}
                    <DateRangeFilter className="w-full md:w-12rem" range={filters.range} custom={filters.custom} onChange={(range, custom) => update({range, custom})}/>
                    <div className="w-full md:w-12rem">
                        <ClearableDropdown value={filters.status} placeholder={t("forms.filter.status")} className="text-xs h-3rem border-round-lg align-items-center" panelClassName="rtl" pt={DROPDOWN_PT}
                                           options={SUBMISSION_STATUSES.map((value) => ({value, label: t(`forms.submission-status.${value}`)}))} clearAriaLabel={t("forms.filter.clear")} onChange={(event) => update({status: event.value})}/>
                    </div>
                    <Button type="button" outlined className="h-3rem text-xs gap-2 flex-none border-round-lg" icon={<Icon icon="mi mi:download" className="text-base"/>} label={t("forms.export.button")} onClick={() => setExporting(true)}/>
                </div>
            </section>
            {error ? (
                <div className="surface-card border-1 border-200 border-round-2xl flex flex-column align-items-center gap-3 p-5">
                    <Message severity="error" text={t(error)}/>
                    <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:refresh" className="text-base"/>} label={t("retry")} onClick={load}/>
                </div>
            ) : (
                <DataTable value={data?.items || []} loading={!data} emptyMessage={<span className="text-xs">{t("forms.responses.empty")}</span>} size="small" rowHover selectionMode="single"
                           lazy paginator={(data?.total || 0) > page.rows} first={page.first} rows={page.rows} totalRecords={data?.total || 0} onPage={(event) => setPage({first: event.first, rows: event.rows})}
                           className="text-xs surface-card border-1 border-200 border-round-2xl overflow-hidden" onRowClick={(event) => setOpen(event.data.key)}>
                    <Column className="text-right" header={t("forms.column.respondent")} body={(item) => (
                        <div className="flex align-items-center gap-2">
                            <strong className="text-xs text-800">{item.respondent || t("forms.detail.no-name")}</strong>
                            {item.source === "test" && <SubmissionTag status="test"/>}
                        </div>
                    )}/>
                    {!formKey && <Column className="text-right" header={t("forms.column.form")} body={(item) => item.form?.title || "—"}/>}
                    <Column className="text-right" header={t("forms.column.submitted")} body={(item) => format.date(item.submittedAt)}/>
                    <Column className="text-right" header={t("forms.column.processing")} body={(item) => (
                        <span className="flex flex-column gap-1 align-items-start">
                            <SubmissionTag status={item.status}/>
                            {item.actions.failed ? <span className="text-red-500">{t("forms.responses.failed", {n: format.number(item.actions.failed)})}</span> : null}
                        </span>
                    )}/>
                    <Column className="text-right" header={t(formKey ? "forms.column.landed-where" : "forms.column.landed")} body={(item) => outputsText(item.outputs)}/>
                    {formKey && <Column className="text-right" header={t("forms.column.version")} body={(item) => (item.version ? t("forms.version", {n: format.number(item.version)}) : "—")}/>}
                    <Column className="text-left" body={(item) => (
                        <Button type="button" text className="text-xs gap-2 py-1" icon={<Icon icon="mi mi:visibility" className="text-base"/>} label={t("forms.responses.details")} onClick={(event) => { event.stopPropagation(); setOpen(item.key); }}/>
                    )}/>
                </DataTable>
            )}
            <SubmissionDrawer submissionKey={open} onClose={() => setOpen(null)} onChanged={load} canManage={canManage}/>
            <ExportDialog visible={exporting} onHide={() => setExporting(false)} forms={forms} toast={toast}
                          initialForm={filters.form} initialRange={filters.range} initialCustom={filters.custom}/>
        </div>
    );
}

